import React, {
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import classNames from 'classnames'
import {
  computedResponsive,
  IResponsiveType,
  responsiveTypes,
  useResponsive,
} from 'Components/hooks/use-responsive'
import { unstable_batchedUpdates } from 'react-dom'
import { SpiderContext } from '../../../pages/_app'

type Grids = number[][]

function makeGrids(count: number, cols: number, rows: number) {
  const screen = cols * rows
  const screens = ~~(count / screen)
  if (!screens) {
    // 数量不足的布局逻辑 优先横向填充
    const realRows = Math.ceil(count / cols)
    const grids: Grids = Array.from({ length: realRows }).map(() => [])
    for (let row = 0; row < realRows; row++) {
      for (let col = 0; col < cols; col++) {
        const current = row * cols + col
        if (current >= count) {
          break
        }
        grids[row].push(current)
      }
    }
    return grids
  }
  // 数量充足的布局逻辑 优先纵向填充
  const grids: Grids = Array.from({ length: rows }).map(() => [])
  for (let i = 0; i < screens; i++) {
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        grids[row].push(screen * i + row * cols + col)
      }
    }
  }
  const lastCols = Math.ceil((count - screen * screens) / rows)
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < lastCols; col++) {
      const current = screen * screens + row * lastCols + col
      if (current >= count) {
        break
      }
      grids[row].push(current)
    }
  }
  return grids
}

export type ResponsiveConfig = {
  [key in IResponsiveType]: number
}

export interface INavigation {
  prevEl?: HTMLElement | null
  nextEl?: HTMLElement | null
}

interface IProps {
  slideCount: number
  cols: ResponsiveConfig
  rows: ResponsiveConfig
  gap?: number
  speed?: number
  slideClass: string
  children(params: { index: number; activeIndex: number }): ReactNode
  navigation?: INavigation
  onSlideChange?(activeIndex: number): void
  className?: string
  tag?: 'ul' | 'ol' | 'div'
}

const ResponsiveSwiper = ({
  slideCount,
  cols,
  rows,
  gap = 0,
  speed = 300,
  slideClass,
  children,
  navigation,
  onSlideChange,
  className,
  tag = 'div',
}: IProps) => {
  const [isClient, setIsClient] = useState(false)
  const [realCols, setRealCols] = useState(0)
  const [realRows, setRealRows] = useState(0)
  // const { isSpider } = useContext(SpiderContext)
  const [grids, setGrids] = useState(() => [
    Array.from({
      length: Math.min(
        Math.max(...responsiveTypes.map((d) => cols[d] * rows[d])),
        slideCount
      ),
    }).map((item, index) => index),
  ])
  const responsive = useResponsive()
  const [activeIndex, setActiveIndex] = useState(0)
  const [visited, setVisited] = useState<Record<number, boolean>>({
    [activeIndex]: true,
  })
  const activeIndexRef = useRef(0)
  const [maxOffset, setMaxOffset] = useState(0)
  const [needTransition, setNeedTransition] = useState(true)

  useEffect(() => {
    unstable_batchedUpdates(() => {
      setRealRows(rows[computedResponsive()])
      setRealCols(cols[computedResponsive()])
      setIsClient(true)
    })
  }, [rows, cols, responsive])

  const screen = useMemo(() => realCols * realRows, [realCols, realRows])

  useEffect(() => {
    if (isClient) {
      unstable_batchedUpdates(() => {
        const screens = ~~(slideCount / screen)
        const lastCols = Math.ceil((slideCount - screen * screens) / realRows)
        setMaxOffset(screens - (realCols - lastCols) / realCols)
        setGrids(makeGrids(slideCount, realCols, realRows))
      })
    }
  }, [slideCount, screen, realCols, realRows, isClient])

  const { prevEl, nextEl } = navigation || {}
  useEffect(() => {
    const handlePrev = () => {
      setNeedTransition(true)
      setActiveIndex((prevState) => Math.max(prevState - 1, 0))
    }
    const handleNext = () => {
      setNeedTransition(true)
      setActiveIndex((prevState) =>
        Math.min(prevState + 1, Math.ceil(maxOffset))
      )
    }
    if (prevEl) {
      prevEl.addEventListener('click', handlePrev)
    }
    if (nextEl) {
      nextEl.addEventListener('click', handleNext)
    }

    return () => {
      if (prevEl) {
        prevEl.removeEventListener('click', handlePrev)
      }
      if (nextEl) {
        nextEl.removeEventListener('click', handleNext)
      }
    }
  }, [prevEl, nextEl, maxOffset])

  useEffect(() => {
    const disableClass = 'swiper-button-disabled'
    if (prevEl) {
      prevEl.classList[activeIndex === 0 ? 'add' : 'remove'](disableClass)
    }
    if (nextEl) {
      nextEl.classList[activeIndex === Math.ceil(maxOffset) ? 'add' : 'remove'](
        disableClass
      )
    }
  }, [activeIndex, prevEl, nextEl, maxOffset])

  useEffect(() => {
    if (activeIndexRef.current !== activeIndex) {
      if (onSlideChange) onSlideChange(activeIndex)
    }
    activeIndexRef.current = activeIndex
  }, [activeIndex, onSlideChange])

  useEffect(() => {
    setVisited((prevState) => ({ ...prevState, [activeIndex]: true }))
  }, [activeIndex])

  const counts = useMemo(() => {
    return responsiveTypes.map((d) => cols[d] * rows[d])
  }, [cols, rows])

  const WrapTag: any = tag

  const InnerTag: any = tag === 'div' ? 'div' : 'li'

  return (
    <React.Fragment>
      <div className={classNames('tw-overflow-hidden', className)}>
        <div
          className={classNames({ container: needTransition })}
          style={{
            transform: `translate3d(-${
              Math.min(activeIndex, maxOffset) * 100
            }%, 0, 0)`,
            margin: -gap / 2,
          }}
        >
          {grids.map((indexes, index) => {
            return (
              <WrapTag
                key={index}
                className={classNames('tw-flex', {
                  'tw-flex-wrap': !isClient,
                })}
              >
                {indexes.map((index) => {
                  const hidden = responsiveTypes.map(
                    (d, i) => index >= counts[i]
                  )
                  const isHidden = hidden.every(Boolean)
                  const isBlock = !hidden.some(Boolean)

                  const needRender = visited[~~(index / screen)]

                  return needRender ? (
                    <InnerTag
                      className={classNames(
                        'tw-flex-none',
                        slideClass,
                        isClient || isBlock
                          ? ''
                          : isHidden
                          ? 'tw-hidden'
                          : [
                              hidden[0] ? 'sm:tw-hidden' : 'sm:tw-block',
                              hidden[1] ? 'md:tw-hidden' : 'md:tw-block',
                              hidden[2] ? 'lg:tw-hidden' : 'lg:tw-block',
                              hidden[3] ? 'xl:tw-hidden' : 'xl:tw-block',
                              hidden[4] ? '2xl:tw-hidden' : '2xl:tw-block',
                            ]
                      )}
                      key={index}
                      style={{ padding: gap / 2 }}
                    >
                      {children({ index, activeIndex })}
                    </InnerTag>
                  ) : null
                })}
              </WrapTag>
            )
          })}
        </div>
        <style jsx>
          {`
            .container {
              transition: transform ${speed}ms ease-out;
            }
          `}
        </style>
      </div>
    </React.Fragment>
  )
}

export default ResponsiveSwiper
